import { h } from 'preact';
import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import {
  PLAYER_FLOW, USER_CLICK, USER_FULLSCREEN,
  USER_KEYBOARD, USER_PIP, USER_SET_FOCUS
} from '../../../../../types';
import { loopThroughSubtitlesTracks } from '../../../../../utils/tracks';
import { BUTTON_LAYER } from '../../../../common/constants';
import { usePlayerContext, useStyles } from '../../../../common/hooks';
import { isEscapeKeyboard, isValidateKeyboard } from '../../../../common/utils/keyboard';
import { connect, withBreakPoints } from '../../../hoc';
import { togglePanel } from '../../../utils';
import { Button } from '../../common';
import { CHAT_NAME, PLAYLIST_NAME, ZAPPING_NAME } from '../../wrappers/Zap/constants';
import { EXP_CONTROL_CONTAINER_STYLES, EXP_CONTROL_ICON_STYLES } from './styles';
import Volume from './volume/Volume';

function ExpControl({
  isAd,
  isDAI,
  isTv,
  volume,
  muted,
  subtitlesAvailable,
  subtitleSelected,
  medium,
  extraSmall,
  small,
  mobile,
  ios,
  isFullscreen,
  pipSupported,
  isLive,
  zappingList,
  programPlayList,
  chatAvailable,
  isPanelOpened
}) {
  const player = usePlayerContext();
  const setStyle = useStyles();
  const fullScreenIconRef = useRef(null);
  const { size: iconSize } = setStyle(EXP_CONTROL_ICON_STYLES);

  const getPriorityPanelName = () => {
    if (isLive && chatAvailable) return CHAT_NAME;
    if (isLive && zappingList?.length > 0) return ZAPPING_NAME;
    if (!isLive && programPlayList?.length > 0) return PLAYLIST_NAME;
    return null;
  };
  const [subtitlesLabel, setSubtitlesLabel] = useState('');
  const [subtitlesAriaLabel, setSubtitlesAriaLabel] = useState('');

  useEffect(() => {
    const currentSub = subtitlesAvailable.find((s) => s.index === subtitleSelected);
    const textLabel = currentSub?.label;
    const ariaLabel = currentSub?.ariaLabel;
    const langLabel = currentSub?.language ? `[${currentSub.language}]` : '';

    setSubtitlesAriaLabel(`Sous-titres ${ariaLabel}`);
    setSubtitlesLabel(`Sous-titres - ${textLabel} ${langLabel} ${mobile ? '' : '(c)'}`);
  }, [subtitleSelected, subtitlesAvailable]);

  const handleFullScreenClick = useCallback((e) => {
    const isKeyboardEvent = e instanceof KeyboardEvent;
    const isValidateKey = isValidateKeyboard(e);
    if (isKeyboardEvent && !isValidateKey && !isEscapeKeyboard(e)) {
      return;
    }

    player.userEvents$.next({
      action: USER_FULLSCREEN,
      value: !isFullscreen,
      source: isKeyboardEvent ? USER_KEYBOARD : USER_CLICK
    });

    player.fullscreen(!isFullscreen);

    if (!isFullscreen) {
      player.userEvents$.next({
        action: USER_SET_FOCUS,
        payload: { element: fullScreenIconRef.current }
      });

      if (!isPanelOpened && !isAd) {
        const name = getPriorityPanelName();
        // Open panel (if possible) when switching to fullscreen
        if (name) {
          togglePanel(
            player,
            { show: true, source: PLAYER_FLOW, name }
          );
        }
      }
    }
  }, [isFullscreen, isPanelOpened, isAd]);

  return (
    <div style={setStyle(EXP_CONTROL_CONTAINER_STYLES)}>
      <Button
        hidden={(isAd && !isDAI) || (!subtitlesAvailable.length)}
        ariaLabel={subtitlesAriaLabel}
        label={subtitlesLabel}
        onClick={() => loopThroughSubtitlesTracks({ player, currentIndex: subtitleSelected, available: subtitlesAvailable, source: USER_CLICK })}
        xs
        name="btn-subtitles"
        type="icon"
        icon={subtitleSelected === -1 ? 'tracks' : 'tracks-active'}
        size={iconSize}
        last
        zIndex={BUTTON_LAYER + 1}// +1 so tootip can be visible when button panel is visible
      />

      <Volume
        volume={volume}
        muted={muted}
        medium={medium}
        extraSmall={extraSmall}
        small={small}
        name="btn-volume"
        disableFocus={false}
        ariaLabel={`${muted ? 'volume désactivé' : 'volume activé'} raccourci touche m`}
        ariaHidden="false"
      />

      <Button
        hidden={!pipSupported || (ios && !pipSupported) || isAd || isTv}
        label="Lecteur réduit (i)"
        onClick={() => {
          player.userEvents$.next({
            action: USER_PIP,
            value: true,
            source: USER_CLICK
          });
          player.requestPIP(true);
        }}
        xs
        name="btn-pip"
        type="icon"
        icon="pip"
        disableFocus
        zIndex={BUTTON_LAYER}
        size={iconSize}
      />

      <Button
        hidden={isTv}
        label={`${isFullscreen ? 'Quitter le plein écran' : 'Plein écran'} (f)`}
        ariaLabel={`${isFullscreen ? 'Quitter le mode plein écran' : 'Plein écran'} raccourci touche f`}
        ariaHidden="false"
        iconRef={fullScreenIconRef}
        onClick={handleFullScreenClick}
        last
        xs
        name="btn-fullscreen"
        disableFocus={false}
        zIndex={BUTTON_LAYER + 1} // +1 so it can be clicked is button panel is visible
        type="icon"
        icon={isFullscreen ? 'fullscreen-on' : 'fullscreen-off'}
        size={iconSize}
      />
    </div>
  );
}

const selector = ({
  ui,
  media,
  volume,
  systemInfo,
  zapping,
  playlist
}) => {
  const { isFullscreen, chatAvailable, panelLiveOption: { show: isPanelOpened } } = ui;
  const { pipSupported } = systemInfo;
  const {
    level,
    muted
  } = volume;
  const {
    subtitlesAvailable,
    subtitleSelected,
    audiosAvailable,
    audioSelected,
    isAd,
    isDAI,
    isTv,
    isLive
  } = media;
  const { list, zappingList = list } = zapping;
  const { playlist: programPlayList } = playlist;

  return ({
    isAd,
    isDAI,
    isTv,
    volume: level,
    muted,
    subtitlesAvailable,
    subtitleSelected,
    audiosAvailable,
    audioSelected,
    isFullscreen,
    pipSupported,
    isLive,
    zappingList,
    programPlayList,
    chatAvailable,
    isPanelOpened
  });
};

export default withBreakPoints(connect(selector)(ExpControl));
